"use client"

import { APP_URL, BOOK_DEMO_URL } from "../../config"
import type { FullTranslation } from "../../lib/i18n"
import { useTr } from "../../lib/use-tr"
import { LocaleLink } from "../locale-link"
import { LocaleSwitcher } from "../locale-switcher"
import { CalendarClock, Menu, X } from "lucide-react"
import Image from "next/image"
import { useState } from "react"

import { cn } from "@/lib/utils"

import { Button } from "@/components/ui/button"

const NAVBAR_T = {
  link_pricing: { FR: "Tarifs", EN: "Pricing" },
  link_blog: { FR: "Blog", EN: "Blog" },
  link_case_studies: { FR: "Cas clients", EN: "Case studies" },
  link_mcp: { FR: "MCP", EN: "MCP" },
  link_about: { FR: "À propos", EN: "About" },
  cta_signin: { FR: "Connexion", EN: "Sign in" },
  cta_demo: { FR: "Réserver une démo", EN: "Book a demo" },
  menu: { FR: "Menu", EN: "Menu" },
  nav_main: { FR: "Navigation principale", EN: "Main navigation" },
  nav_mobile: { FR: "Navigation mobile", EN: "Mobile navigation" },
} satisfies FullTranslation

export function Navbar() {
  const tr = useTr(NAVBAR_T)
  const [open, setOpen] = useState(false)
  const links = [
    { key: "link_pricing", href: "/#pricing" },
    { key: "link_case_studies", href: "/cas-clients" },
    { key: "link_mcp", href: "/mcp" },
    { key: "link_blog", href: "/blog" },
    { key: "link_about", href: "/about" },
  ] as const

  return (
    <header
      className={cn(
        "fixed left-1/2 z-50 w-[94%] max-w-6xl -translate-x-1/2 overflow-hidden border backdrop-blur-xl",
        // The open panel sits over page content, so it needs a near-opaque
        // surface to stay readable.
        open ? "bg-background/95" : "bg-background/75",
        "shadow-soft-inset top-3 lg:top-6",
        // 1.5rem matches the pill radius of the closed mobile bar (half its
        // height), so the outline stays put while the menu unrolls under it.
        // Desktop keeps rounded-full: it never expands.
        "rounded-3xl md:rounded-full",
      )}
      role="banner"
    >
      <div className="flex items-center justify-between gap-4 px-4 py-2 sm:px-5 sm:py-2.5">
        <LocaleLink
          href="/"
          prefetch={false}
          className="font-display text-md flex items-center gap-2 font-semibold tracking-tight"
        >
          {/* No priority: the logo is hidden below md, so preloading it only
              competes with the stylesheets for the mobile critical path, and
              the LCP element is the hero h1 rather than this image. */}
          <Image
            src="/grezy/grezy.svg"
            alt="Grezy"
            width={44}
            height={24}
            className="h-6 w-auto max-md:hidden dark:invert"
          />
          <span>AutoLeads</span>
        </LocaleLink>
        <nav className="max-md:hidden" aria-label={tr("nav_main")}>
          <ul className="text-2sm flex items-center gap-7 font-medium">
            {links.map((link) => (
              <li key={link.key}>
                <LocaleLink
                  href={link.href}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {tr(link.key)}
                </LocaleLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="flex items-center gap-1.5">
          <LocaleSwitcher className="text-muted-foreground hover:text-foreground text-2sm inline-flex h-8 items-center justify-center rounded-full px-2 font-medium transition-colors max-md:hidden" />
          <LocaleLink
            href={APP_URL}
            prefetch={false}
            className="text-muted-foreground hover:text-foreground text-2sm inline-flex h-8 items-center justify-center rounded-full px-2 font-medium transition-colors"
          >
            {tr("cta_signin")}
          </LocaleLink>
          <a href={BOOK_DEMO_URL} target="_blank" rel="noreferrer">
            <Button
              variant="cta"
              shape="full"
              size="sm"
              aria-label={tr("cta_demo")}
              className="cta-shine text-2sm h-8 px-3.5 font-medium max-md:w-8 max-md:px-0"
            >
              <CalendarClock className="size-3.5 md:mr-1" aria-hidden />
              <span className="max-md:hidden">{tr("cta_demo")}</span>
            </Button>
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={tr("menu")}
            className="text-muted-foreground hover:text-foreground inline-flex size-8 items-center justify-center rounded-full transition-colors md:hidden"
          >
            {open ? (
              <X className="size-4" aria-hidden />
            ) : (
              <Menu className="size-4" aria-hidden />
            )}
          </button>
        </div>
      </div>
      {/* Kept mounted so the panel unrolls on the way out too; the grid row
          animates from 0fr to 1fr, which the clipped child turns into height. */}
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-300 ease-out md:hidden",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <nav
          id="mobile-nav"
          aria-label={tr("nav_mobile")}
          aria-hidden={!open}
          inert={!open}
          className="overflow-hidden"
        >
          <ul className="border-border/60 text-2sm flex flex-col gap-1 border-t px-4 py-3 font-medium">
            {links.map((link) => (
              <li key={link.key}>
                <LocaleLink
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="text-muted-foreground hover:text-foreground block py-2 transition-colors"
                >
                  {tr(link.key)}
                </LocaleLink>
              </li>
            ))}
            <li>
              <a
                href={APP_URL}
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground block py-2 transition-colors"
              >
                {tr("cta_signin")}
              </a>
            </li>
            <li>
              <LocaleSwitcher className="text-muted-foreground hover:text-foreground block py-2 transition-colors" />
            </li>
            <li className="pt-1">
              <a
                href={BOOK_DEMO_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => setOpen(false)}
              >
                <Button
                  variant="cta"
                  shape="lg"
                  size="sm"
                  className="text-2sm h-9 w-full font-medium"
                >
                  <CalendarClock className="mr-1 size-3.5" aria-hidden />
                  {tr("cta_demo")}
                </Button>
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  )
}
